import { type SelectedChanges } from './commit-contract';
import {
  changeId,
  errorsThatBlock,
  type DiffChange,
  type DiffClassification,
  type DiffResult
} from './diff';

const isBlocked = (change: DiffChange, selected: ReadonlySet<string>, result: DiffResult) =>
  errorsThatBlock(change, selected, result).length > 0;

/** The selected changes, grouped by classification. Blocked rows are left out even if selected. */
export const selectedChanges = (
  result: DiffResult,
  selected: ReadonlySet<string>
): SelectedChanges => {
  // Import only the first selected copy of an exact repeat.
  const takenCopies = new Set<number>();
  const importable = result.changes.filter((change) => {
    if (!selected.has(changeId(change)) || isBlocked(change, selected, result)) {
      return false;
    }
    if (change.line === undefined) {
      return true;
    }
    const copyOf = change.repeatOf ?? change.line;
    if (takenCopies.has(copyOf)) {
      return false;
    }
    takenCopies.add(copyOf);
    return true;
  });
  const pick = (classification: DiffChange['classification']) =>
    importable.filter((change) => change.classification === classification);
  return { added: pick('added'), updated: pick('updated'), removed: pick('removed') };
};

/** Selected rows that cannot be written, and so will be left behind. */
export const malformedCount = (result: DiffResult, selected: ReadonlySet<string>): number =>
  result.changes.filter(
    (change) => selected.has(changeId(change)) && isBlocked(change, selected, result)
  ).length;

export const selectedCount = (changes: SelectedChanges): number =>
  changes.added.length + changes.updated.length + changes.removed.length;

const SELECTABLE = new Set<DiffClassification>(['removed', 'added', 'updated']);

export const isSelectable = (change: DiffChange): boolean => SELECTABLE.has(change.classification);

/** Everything is selected by default; the user skips rows they don't want. */
export const defaultSelection = (result: DiffResult): Set<string> =>
  new Set(result.changes.filter(isSelectable).map(changeId));

/** Selects rows that became selectable (e.g. an unchanged row edited into an update), keeping the rest as the user left them. */
export const selectNewlySelectable = (
  current: ReadonlySet<string>,
  previous: DiffResult | undefined,
  next: DiffResult | undefined
): Set<string> => {
  const before = new Set((previous?.changes ?? []).filter(isSelectable).map(changeId));
  const updated = new Set(current);
  for (const change of next?.changes ?? []) {
    if (isSelectable(change) && !before.has(changeId(change))) {
      updated.add(changeId(change));
    }
  }
  return updated;
};
